import { React } from '@/lib/react';
import { useState, useEffect } from 'react';
import { lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { UserProvider } from "./context/UserContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { MultiStepLoader } from "@/components/ui/multi-step-loader";
import { supabase } from "../supabaseClient";
import { BrandMediaProvider } from './context/BrandMediaContext';

// Add lazy imports for pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const NotFound = lazy(() => import('./pages/NotFound'));
const SignUpPage = lazy(() => import('./pages/SignUpPage'));
const SignInPage = lazy(() => import('./pages/SignInPage'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Properties = lazy(() => import('./pages/Properties'));
const PropertyForm = lazy(() => import('./pages/PropertyForm'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const ToneOnboardingPage = lazy(() => import('./pages/ToneOnboardingPage'));
const Posts = lazy(() => import('./pages/Posts'));
const Features = lazy(() => import('./pages/Features'));
// Lazy load AuthenticatedLayout
const AuthenticatedLayout = lazy(() => import('@/components/layout/AuthenticatedLayout'));

const queryClient = new QueryClient();

const ALL_LOADING_MESSAGES = [
  { text: "Unlocking the front door... (Had to jiggle the key a little)" },
  { text: "Fluffing the throw pillows... (Buyers love a well-placed cushion)" },
  { text: "Staging the data... (Looks even better than the photos)" },
  { text: "Checking comps in a 0.5 mile radius... (Adjusted for \"charm\" and wishful thinking)" },
  { text: "Negotiating with the MLS... (Still no API, but we got what we needed)" },
  { text: "Printing 87 flyers... (Immediately outdated, still pretty)" },
  { text: "Cropping out the power lines... (Now that's \"unobstructed views\")" },
  { text: "Rewriting the listing description... (Added \"chef's kitchen\" to the galley)" },
  { text: "Syncing open house snacks... (Don't forget the Costco cookies)" },
  { text: "Final walkthrough... (Closet door stays closed for a reason)" },
];

// Private route component to protect routes that require authentication
const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { session, isLoading } = useAuth();

  if (isLoading) {
    return <MultiStepLoader loadingStates={ALL_LOADING_MESSAGES.slice(0, 4)} loading={true} />;
  }

  if (!session) {
    return <Navigate to="/signin" replace />;
  }

  return <>{children}</>;
};

const App = () => {
  const selectedLoadingStates = React.useMemo(() => {
    const shuffled = [...ALL_LOADING_MESSAGES].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 4);
  }, []);

  return (
    <BrandMediaProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <UserProvider>
            <TooltipProvider>
              <BrowserRouter>
                <Suspense fallback={<div>Loading...</div>}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/signup" element={<SignUpPage />} />
                    <Route path="/signin" element={<SignInPage />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/features" element={<Features />} />
                    
                    {/* Protected route uses lazy AuthenticatedLayout */}
                    <Route
                      path="/app/*"
                      element={
                        <PrivateRoute>
                          <Suspense fallback={<MultiStepLoader loadingStates={selectedLoadingStates} loading={true} />}>
                            <AuthenticatedLayout />
                          </Suspense>
                        </PrivateRoute>
                      }
                    />

                    {/* 404 route */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
              <Toaster />
              <Sonner />
            </TooltipProvider>
          </UserProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrandMediaProvider>
  );
};

export default App;
